import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { ComponentValidationResult, ComponentViewModel, ComponentsInPipeClassViewModel } from 'src/app/shared/models/autogenerated-piping';
import { CommentViewModel } from '../../shared/models/comment.model';
import { Observable } from 'rxjs';

@Component({
    selector: 'app-piping-components',
    styleUrls: ['piping-components.component.scss'],
    templateUrl: './piping-components.component.html',
    standalone: false
})
export class PipingComponentsComponent implements OnInit{
    @Input() pipeClassViewModelComponents: ComponentsInPipeClassViewModel[];
    @Input() canEdit$: Observable<boolean>;
    /** True for any user who arrived from the revisions dashboard (authors + reviewers).
     *  Used to show the comments section even for reviewers who cannot edit fields. */
    @Input() isInRevisionWorkflow: boolean = false;
    @Input() currentRevisionName: string; // Current revision name for comparison
    @Input() isRevisionInProgress: boolean = false; // Whether the revision is being edited (not published)
    @Input() pipeClassId: string; // For comments section
    @Input() revisionId: string; // For comments section
    @Input() comments: CommentViewModel[] = []; // Comments for components tab
    @Input() commentsLoading$: Observable<boolean>; // True while comments are being fetched
    @Output() editComponentGroupNotesEvent = new EventEmitter<string>();
    @Output() commentsChanged = new EventEmitter<CommentViewModel[]>();
    @Output() saveCommentsRequested = new EventEmitter<void>(); // New event for auto-save
    @Output() editIndividualComponentNotesEvent = new EventEmitter<ComponentsInPipeClassViewModel>();
    @Output() addComponentsToGroupEvent = new EventEmitter<string>();
    @Output() addGroupNoteEvent = new EventEmitter<string>(); // New event for adding group note
    @Output() removeComponentEvent = new EventEmitter<ComponentsInPipeClassViewModel>(); // New event for removing component
    @Output() updateNpsEvent = new EventEmitter<{component: ComponentsInPipeClassViewModel, newNps: string}>(); // New event for updating NPS
    @Output() updateSchRatEvent = new EventEmitter<{component: ComponentsInPipeClassViewModel, newSchRat: string}>();
    @Output() reorderComponentsEvent = new EventEmitter<{groupName: string, components: ComponentsInPipeClassViewModel[]}>(); // New event for reordering components

    @Input() isCompareMode: boolean = false;
    /** Components from the previous revision; filtered per group when passed to app-pipe-component-details. */
    @Input() previousComponents: ComponentsInPipeClassViewModel[] = [];
    /** Catalog validation results keyed by ComponentViewModel.componentId, passed down from piping-tabs-parent. */
    @Input() componentValidationResults: Map<string, ComponentValidationResult> = new Map();
    /** Bubbles up from app-pipe-component-details when the user requests an in-place catalog upgrade. */
    @Output() replaceComponentEvent = new EventEmitter<{ current: ComponentsInPipeClassViewModel; latestVersion: ComponentViewModel }>();

    //... ['COMPONENT_GROUP_NAME', 'DISPLAY_NAME'] ...
    protected orderedComponentGroups = [
        ['Pipe', 'Pipes'],
        ['Nipples/Swages','Nipples/Swages'],
        ['Fittings', 'Fittings'],
        ['Tube Fittings', 'Tube Fittings'],
        ['Flanges', 'Flanges'],
        ['Blanks and Spacers', 'Blanks and Spacers'],
        ['Gaskets', 'Gaskets'],
        ['Bolts', 'Bolting'],
        ['MISC', 'MISC']
    ];

    protected nonEmptyComponentGroups: string[][];

    ngOnInit(): void {
        this.nonEmptyComponentGroups = this.orderedComponentGroups
            .filter(componentGroup =>
                this.getComponentsByGroup(componentGroup[0]).length > 0
            )
    }

    getComponentsByGroup(group: string): ComponentsInPipeClassViewModel[] {
        if (this.pipeClassViewModelComponents && this.pipeClassViewModelComponents.length > 0 ) {
            const groupComponents: ComponentsInPipeClassViewModel[] = this.pipeClassViewModelComponents
                .filter((componentInPipeClass: ComponentsInPipeClassViewModel) => componentInPipeClass.component.group == group);
            return this.filterInactiveDuplicateComponents(groupComponents);
        }
        return [];
    }

    /**
     * Returns the previous revision's components for a given group key.
     * Passed to `app-pipe-component-details` so per-group row diffs can be computed.
     */
    getPreviousComponentsByGroup(group: string): ComponentsInPipeClassViewModel[] {
        if (!this.previousComponents || this.previousComponents.length === 0) { return []; }
        const previousGroupComponents: ComponentsInPipeClassViewModel[] = this.previousComponents
            .filter((componentInPipeClass: ComponentsInPipeClassViewModel) => componentInPipeClass.component?.group === group);
        return this.filterInactiveDuplicateComponents(previousGroupComponents);
    }

    private filterInactiveDuplicateComponents(components: ComponentsInPipeClassViewModel[]): ComponentsInPipeClassViewModel[] {
        const activeKeys: Set<string> = new Set<string>();
        components.forEach((componentInPipeClass: ComponentsInPipeClassViewModel) => {
            if (componentInPipeClass.component?.isActive !== false) {
                activeKeys.add(this.buildComponentDuplicateKey(componentInPipeClass));
            }
        });

        return components.filter((componentInPipeClass: ComponentsInPipeClassViewModel) => {
            if (componentInPipeClass.component?.isActive !== false) {
                return true;
            }
            return !activeKeys.has(this.buildComponentDuplicateKey(componentInPipeClass));
        });
    }

    private buildComponentDuplicateKey(componentInPipeClass: ComponentsInPipeClassViewModel): string {
        const code: string = componentInPipeClass.component?.code?.trim().toLowerCase() ?? '';
        const type: string = componentInPipeClass.component?.type?.trim().toLowerCase() ?? '';
        const group: string = componentInPipeClass.component?.group?.trim().toLowerCase() ?? '';
        return `${group}|${type}|${code}`;
    }

    onEditGroupNotes(groupName: string): void {
    // Ensure we emit the actual group key (e.g. 'Pipe') rather than the display name ('Pipes')
    const actualGroupName = this.getActualGroupName(groupName);
    this.editComponentGroupNotesEvent.emit(actualGroupName);
    }

    onEditComponentNotes(component: ComponentsInPipeClassViewModel): void {
        this.editIndividualComponentNotesEvent.emit(component);
    }

    onAddComponents(groupName: string): void {
        // Map display name back to actual group name before emitting
        const actualGroupName = this.getActualGroupName(groupName);
        this.addComponentsToGroupEvent.emit(actualGroupName);
    }

    onAddGroupNote(groupName: string): void {
        // Map display name back to actual group name before emitting
        const actualGroupName = this.getActualGroupName(groupName);
        this.addGroupNoteEvent.emit(actualGroupName);
    }

    onRemoveComponent(component: ComponentsInPipeClassViewModel): void {
        this.removeComponentEvent.emit(component);
    }

    onReplaceComponent(event: { current: ComponentsInPipeClassViewModel; latestVersion: ComponentViewModel }): void {
        this.replaceComponentEvent.emit(event);
    }

    onUpdateNps(event: {component: ComponentsInPipeClassViewModel, newNps: string}): void {
        this.updateNpsEvent.emit(event);
    }

    onUpdateSchRat(event: {component: ComponentsInPipeClassViewModel, newSchRat: string}): void {
        this.updateSchRatEvent.emit(event);
    }

    onReorderComponents(event: {groupName: string, components: ComponentsInPipeClassViewModel[]}): void {
        // Map display name back to actual group name before emitting
        const actualGroupName = this.getActualGroupName(event.groupName);
        this.reorderComponentsEvent.emit({
            groupName: actualGroupName,
            components: event.components
        });
    }

    /**
     * Map display name back to actual group name
     * @param displayName The display name (e.g., "Pipes")
     * @returns The actual group name (e.g., "Pipe")
     */
    private getActualGroupName(displayName: string): string {
        const mapping = this.orderedComponentGroups.find(([actual, display]) => display === displayName);
        return mapping ? mapping[0] : displayName; // Return actual name, or fallback to display name
    }

    /**
     * Handle comments changed event from child comments component
     */
    onCommentsChanged(comments: CommentViewModel[]): void {
        if (comments && Array.isArray(comments)) {
            // Emit to parent component for centralized management
            this.commentsChanged.emit(comments);
        }
    }

    /**
     * Handle save comments request from child comments component
     */
    onSaveCommentsRequested(): void {
        // Forward the save request to parent component
        this.saveCommentsRequested.emit();
    }
}
